import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import api from '../services/api'
import AvisoUltimaNota from '../components/AvisoUltimaNota'
import NovoClienteModal from '../components/NovoClienteModal'
import CidadeSelect from '../components/CidadeSelect'
import ServicoNFSeSelect from '../components/ServicoNFSeSelect'

// Converte string numérica pro formato JS — só remove separador de milhar (.)
// quando há vírgula decimal (formato BR, ex: "2.569,53"); sem vírgula, assume
// que já está em formato simples (ex: "5.00" vindo de toFixed/cadastro) e não
// toca no ponto, senão "5.00" virava 500.
function parseNumBr(v: string): number {
  const s = String(v ?? '').trim()
  if (s.includes(',')) return Number(s.replace(/\./g, '').replace(',', '.')) || 0
  return Number(s) || 0
}

interface Cliente {
  cli_cod:    string
  cli_mat:    string
  cli_nom:    string
  cli_fan:    string
  cli_fj:     string
  cli_ins:    string
  cli_est:    string
  cli_contri: string
  bloqueado?: boolean
  end_logradouro?: string
  end_numero?:     string
  end_bairro?:     string
  end_cidade?:     string
  end_uf?:         string
}

interface Endereco {
  logradouro: string
  numero:     string
  bairro:     string
  cidade:     string
  uf:         string
  cep:        string
  ibge?:      string
}

interface Empresa {
  empcod: number
  empnom: string
  empfan: string
  empcgc: string
  empest: string
  empsigla: string
}

interface Resultado {
  ok:          boolean
  chaveAcesso?: string
  xMotivo?:    string
  erro?:       string
  serie?:      string | number
  numero?:     number
  nDPS?:       number
  avisoBanco?: string
}

function formatarDocumento(doc: string) {
  const d = doc.replace(/\D/g, '')
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  return doc
}

const CST_PIS_COFINS = [
  { cod: '01', desc: 'Operação tributável — alíquota básica' },
  { cod: '02', desc: 'Operação tributável — alíquota diferenciada' },
  { cod: '03', desc: 'Operação tributável — alíquota por unidade de medida' },
  { cod: '04', desc: 'Operação tributável monofásica — revenda a alíquota zero' },
  { cod: '05', desc: 'Operação tributável por substituição tributária' },
  { cod: '06', desc: 'Operação tributável a alíquota zero' },
  { cod: '07', desc: 'Operação isenta da contribuição' },
  { cod: '08', desc: 'Operação sem incidência da contribuição' },
  { cod: '09', desc: 'Operação com suspensão da contribuição' },
  { cod: '49', desc: 'Outras operações de saída' },
]

function dataAtual() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function EmitirNFSe() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const copyId = searchParams.get('copyId') ? Number(searchParams.get('copyId')) : null
  const editId = searchParams.get('editId') ? Number(searchParams.get('editId')) : null
  const copyEmpcod = searchParams.get('empcod') ? Number(searchParams.get('empcod')) : null

  const [etapa, setEtapa]   = useState(0)
  const [busca, setBusca]   = useState('')
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [buscando, setBuscando] = useState(false)
  const [cliente, setCliente]   = useState<Cliente | null>(null)
  const [endereco, setEndereco] = useState<Endereco | null>(null)
  // IBGE do município do TOMADOR (endNac.cMun da DPS) — separado do resto do endereço
  // porque em cópia/edição de nota rejeitada o endereço vem do snapshot da nota antiga
  // (não pode ser sobrescrito), mas o IBGE sempre precisa ser buscado do cadastro atual.
  const [endCMunToma, setEndCMunToma] = useState('')
  const [modalNovo, setModalNovo] = useState(false)

  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresa,  setEmpresa]  = useState<Empresa | null>(null)

  useEffect(() => {
    api.get('/erp/empresas').then(r => {
      setEmpresas(r.data)
      if (r.data.length > 0) setEmpresa(r.data[0])
    }).catch(() => {})
  }, [])

  // Copiando outra nota: usa a empresa dela como ponto de partida (usuário pode trocar)
  useEffect(() => {
    if (copyEmpcod !== null && empresas.length > 0) {
      const emp = empresas.find(e => e.empcod === copyEmpcod)
      if (emp) setEmpresa(emp)
    }
  }, [copyEmpcod, empresas])

  const [serie,      setSerie]      = useState('7000')
  const [nDPS,       setNDPS]       = useState('')

  useEffect(() => {
    if (editId) return // edição reaproveita a série/número da própria DPS, não busca um novo
    api.get(`/nfse-nacional/proximonumero/${empresa?.empcod ?? 1}`)
      .then(r => { setSerie(String(r.data.serie ?? '7000')); setNDPS(String(r.data.numero ?? '')) })
      .catch(() => {})
  }, [empresa, editId])

  const [xDescServ,  setXDescServ]  = useState('')
  const [competencia, setCompetencia] = useState(dataAtual())
  const [servicoId,  setServicoId]  = useState<number | null>(null)
  const [servicoDes, setServicoDes] = useState('')
  const [cTribNac,   setCTribNac]   = useState('')
  const [cTribMun,   setCTribMun]   = useState('')
  const [cTribMunPadrao, setCTribMunPadrao] = useState('') // valor cadastrado no serviço, sem exceção de cidade
  const [cNBS,       setCNBS]       = useState('')
  const [cLocPrestCod, setCLocPrestCod] = useState('')
  const [cLocPrestDes, setCLocPrestDes] = useState('')
  const [cLocPrest,    setCLocPrest]    = useState('')  // IBGE
  const [cLocPrestUf,  setCLocPrestUf]  = useState('')

  // Código de Tributação Municipal por cidade — cadastrado em NFS-e Nacional → Tributação Municipal
  const [codigosMunicipio, setCodigosMunicipio] = useState<{ id: number; cid_cod: number; cod_tributacao_municipal: string; aliquota_iss: string | null }[]>([])
  const [vServ,      setVServ]      = useState('')
  const [pAliq,      setPAliq]      = useState('')
  const [pAliqPadrao, setPAliqPadrao] = useState('') // alíquota do serviço, usada quando a cidade não tem a sua
  const [tpRetISSQN, setTpRetISSQN] = useState<'1' | '2'>('1')

  useEffect(() => {
    api.get('/nfse-servicos/municipios').then(r => setCodigosMunicipio(r.data)).catch(() => setCodigosMunicipio([]))
  }, [])

  // Cidade com código/alíquota cadastrados usa eles; o que faltar volta pro padrão do serviço
  useEffect(() => {
    if (!servicoId) return
    const encontrado = codigosMunicipio.find(m => String(m.cid_cod) === String(cLocPrestCod))
    setCTribMun(encontrado?.cod_tributacao_municipal || cTribMunPadrao)
    setPAliq(encontrado?.aliquota_iss != null ? Number(encontrado.aliquota_iss).toFixed(2) : pAliqPadrao)
  }, [cLocPrestCod, codigosMunicipio, servicoId, cTribMunPadrao, pAliqPadrao])

  // Tributação federal
  const [piscofinsCst,    setPiscofinsCst]    = useState('07')
  const [vBCPisCofins,    setVBCPisCofins]    = useState('')
  const [pAliqPis,        setPAliqPis]        = useState('')
  const [pAliqCofins,     setPAliqCofins]     = useState('')
  const [descContSocRet,  setDescContSocRet]  = useState('')
  const [vIRRF,           setVIRRF]           = useState('')
  const [vContSocRet,     setVContSocRet]     = useState('')
  const [vRetCP,          setVRetCP]          = useState('')
  const [pInss,           setPInss]           = useState('') // % do serviço; vazio = INSS digitado à mão
  // Total dos tributos
  const [pTotFed, setPTotFed] = useState('')
  const [pTotEst, setPTotEst] = useState('')
  const [pTotMun, setPTotMun] = useState('')
  const [xInfComp, setXInfComp] = useState('')

  const [endLogradouro, setEndLogradouro] = useState('')
  const [endNumero,     setEndNumero]     = useState('SN')
  const [endBairro,     setEndBairro]     = useState('')
  const [endCep,        setEndCep]        = useState('')

  // Financeiro
  const [financeiroModal, setFinanceiroModal] = useState(false)
  const [finQtdParcelas, setFinQtdParcelas]   = useState(1)
  const [finIntervalo, setFinIntervalo]       = useState(0)
  const [finCondDes,   setFinCondDes]         = useState('')
  const [finParcelas, setFinParcelas]         = useState<{ numero: string; vencimento: string; valor: string }[]>([])
  const [finContaCod, setFinContaCod]         = useState('')
  const [finContaNome, setFinContaNome]       = useState('')
  const [finContaResultados, setFinContaResultados] = useState<{ dep_cod: string; dep_des: string }[]>([])
  const [finContaAberto, setFinContaAberto]   = useState(false)

  const [erroEtapa1, setErroEtapa1] = useState<string | null>(null)
  const [enviando, setEnviando]     = useState(false)
  const [resultado, setResultado]   = useState<Resultado | null>(null)

  const buscarTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleBusca(val: string) {
    setBusca(val)
    if (buscarTimer.current) clearTimeout(buscarTimer.current)
    if (val.length < 2) { setClientes([]); return }
    setBuscando(true)
    buscarTimer.current = setTimeout(async () => {
      try {
        const r = await api.get('/erp/clientes/lista', { params: { busca: val, limit: 10 } })
        setClientes(r.data.clientes ?? r.data ?? [])
      } catch { setClientes([]) }
      finally { setBuscando(false) }
    }, 350)
  }

  function selecionarCliente(c: Cliente) {
    setCliente(c)
    setClientes([])
    setBusca('')
    api.get(`/erp/clientes/${c.cli_cod}/endereco`)
      .then(r => {
        setEndereco(r.data?.logradouro ? r.data : null)
        setEndCMunToma(r.data?.ibge || '')
      })
      .catch(() => {})
    // Sugere o vencimento do financeiro com base na condição de pagamento do cliente
    api.get(`/erp/clientes/${c.cli_cod}/condicao-pagamento`)
      .then(r => {
        setFinIntervalo(r.data?.dias ?? 0)
        setFinCondDes(r.data?.des ?? '')
      })
      .catch(() => { setFinIntervalo(0); setFinCondDes('') })
    setEtapa(1)
  }

  useEffect(() => {
    if (endereco) {
      setEndLogradouro(endereco.logradouro ?? '')
      setEndNumero(endereco.numero ?? 'SN')
      setEndBairro(endereco.bairro ?? '')
      setEndCep(endereco.cep ?? '')
    }
  }, [endereco])

  // Carrega dados de outra nota pra copiar — competência sempre vira hoje,
  // e série/número ganham valor novo automaticamente (não são copiados)
  useEffect(() => {
    if (!copyId) return
    api.get(`/nfse-nacional/${copyId}/copia?empcod=${copyEmpcod ?? 1}`).then(r => {
      const d = r.data
      // nota de locação de bens móveis (99.04.01) abre na tela própria
      if (d.locacao) { navigate(`/nfse/locacao?${searchParams.toString()}`, { replace: true }); return }
      if (d.tomador) {
        setCliente(d.tomador); setBusca(d.tomador.cli_nom ?? '')
        // Só o IBGE do cadastro atual do cliente — o resto do endereço abaixo vem do
        // snapshot da nota antiga de propósito, não pode ser sobrescrito por esse fetch.
        api.get(`/erp/clientes/${d.tomador.cli_cod}/endereco`)
          .then(r2 => setEndCMunToma(r2.data?.ibge || ''))
          .catch(() => {})
      }
      if (d.endLogradouro) setEndLogradouro(d.endLogradouro)
      if (d.endNumero)     setEndNumero(d.endNumero)
      if (d.endBairro)     setEndBairro(d.endBairro)
      if (d.endCep)        setEndCep(d.endCep)
      if (d.cLocPrest)     setCLocPrest(d.cLocPrest)
      if (d.xDescServ)     setXDescServ(d.xDescServ)
      if (d.cTribNac)      setCTribNac(d.cTribNac)
      if (d.cTribMun)      setCTribMun(d.cTribMun)
      if (d.cNBS)          setCNBS(d.cNBS)
      if (d.vServ)         setVServ(d.vServ)
      if (d.pAliq)         setPAliq(d.pAliq)
      if (d.tpRetISSQN)    setTpRetISSQN(d.tpRetISSQN)
      if (d.piscofinsCst)  setPiscofinsCst(d.piscofinsCst)
      if (d.vBCPisCofins)  setVBCPisCofins(d.vBCPisCofins)
      if (d.pAliqPis)      setPAliqPis(d.pAliqPis)
      if (d.pAliqCofins)   setPAliqCofins(d.pAliqCofins)
      if (d.vIRRF)         setVIRRF(d.vIRRF)
      if (d.vContSocRet)   setVContSocRet(d.vContSocRet)
      if (d.vRetCP)        setVRetCP(d.vRetCP)
      if (d.pTotFed)       setPTotFed(d.pTotFed)
      if (d.pTotEst)       setPTotEst(d.pTotEst)
      if (d.pTotMun)       setPTotMun(d.pTotMun)
      setCompetencia(dataAtual())
      setEtapa(1)
    }).catch(() => {})
  }, [copyId, copyEmpcod])

  // Editando uma DPS pendente/rejeitada — mantém a mesma série/número/competência,
  // pra reenviar como correção da mesma declaração (não cria uma nova numeração)
  useEffect(() => {
    if (!editId) return
    api.get(`/nfse-nacional/${editId}/copia?empcod=${copyEmpcod ?? 1}`).then(r => {
      const d = r.data
      // nota de locação de bens móveis (99.04.01) abre na tela própria
      if (d.locacao) { navigate(`/nfse/locacao?${searchParams.toString()}`, { replace: true }); return }
      if (d.tomador) {
        setCliente(d.tomador); setBusca(d.tomador.cli_nom ?? '')
        // Só o IBGE do cadastro atual do cliente — o resto do endereço abaixo vem do
        // snapshot da nota antiga de propósito, não pode ser sobrescrito por esse fetch.
        api.get(`/erp/clientes/${d.tomador.cli_cod}/endereco`)
          .then(r2 => setEndCMunToma(r2.data?.ibge || ''))
          .catch(() => {})
      }
      if (d.endLogradouro) setEndLogradouro(d.endLogradouro)
      if (d.endNumero)     setEndNumero(d.endNumero)
      if (d.endBairro)     setEndBairro(d.endBairro)
      if (d.endCep)        setEndCep(d.endCep)
      if (d.cLocPrest)     setCLocPrest(d.cLocPrest)
      if (d.xDescServ)     setXDescServ(d.xDescServ)
      if (d.cTribNac)      setCTribNac(d.cTribNac)
      if (d.cTribMun)      setCTribMun(d.cTribMun)
      if (d.cNBS)          setCNBS(d.cNBS)
      if (d.vServ)         setVServ(d.vServ)
      if (d.pAliq)         setPAliq(d.pAliq)
      if (d.tpRetISSQN)    setTpRetISSQN(d.tpRetISSQN)
      if (d.piscofinsCst)  setPiscofinsCst(d.piscofinsCst)
      if (d.vBCPisCofins)  setVBCPisCofins(d.vBCPisCofins)
      if (d.pAliqPis)      setPAliqPis(d.pAliqPis)
      if (d.pAliqCofins)   setPAliqCofins(d.pAliqCofins)
      if (d.vIRRF)         setVIRRF(d.vIRRF)
      if (d.vContSocRet)   setVContSocRet(d.vContSocRet)
      if (d.vRetCP)        setVRetCP(d.vRetCP)
      if (d.pTotFed)       setPTotFed(d.pTotFed)
      if (d.pTotEst)       setPTotEst(d.pTotEst)
      if (d.pTotMun)       setPTotMun(d.pTotMun)
      if (d.serie)         setSerie(String(d.serie))
      if (d.numero)        setNDPS(String(d.numero))
      setCompetencia(d.dCompet || dataAtual())
      setEtapa(1)
    }).catch(() => {})
  }, [editId, copyEmpcod])

  async function emitir() {
    if (!cliente) return
    if (cliente.bloqueado) { alert('Cliente bloqueado — não é possível emitir documentos para este cliente.'); return }
    const docToma = cliente.cli_mat.replace(/\D/g, '')
    if (!docToma || !xDescServ.trim() || !vServ) {
      alert('Preencha: tomador, descrição do serviço e valor.')
      return
    }
    if (endCep.length !== 8 || !endLogradouro.trim() || !endBairro.trim()) {
      alert('Preencha o endereço completo do tomador (CEP, Logradouro, Bairro).')
      return
    }

    const dCompet = competencia
    const pAliqNum = parseNumBr(pAliq)
    const pAliqDec = pAliqNum > 0 ? pAliqNum / 100 : 0

    setEnviando(true)
    setResultado(null)
    try {
      const r = await api.post('/nfse-nacional/emitir', {
        empcod:       empresa?.empcod ?? 1,
        serie:        serie.trim() || undefined,
        nDPS:         nDPS ? Number(nDPS) : undefined,
        docToma,
        nomeToma:     cliente.cli_nom,
        endLogradouro: endLogradouro,
        endNumero:    endNumero || 'SN',
        endBairro:    endBairro,
        // endCMun é o município do ENDEREÇO do tomador (endNac), não o local onde o
        // serviço foi prestado — usa o IBGE da cidade cadastrada do cliente; cLocPrest
        // só entra como fallback se o cliente não tiver cidade cadastrada com IBGE.
        endCMun:      endCMunToma || cLocPrest || '4307708',
        endCep:       endCep,
        xDescServ:    xDescServ.trim(),
        dCompet,
        cTribNac:     cTribNac.trim() || '010101',
        cTribMun:     cTribMun.trim() || undefined,
        cNBS:         cNBS.trim() || undefined,
        cLocPrest:    cLocPrest.trim() || '4307708',
        vServ:        parseNumBr(vServ),
        pAliq:        pAliqDec,
        tpRetISSQN,
        // Tributação federal
        piscofinsCst,
        vBCPisCofins:   vBCPisCofins  ? parseNumBr(vBCPisCofins)  || undefined : undefined,
        pAliqPis:       pAliqPis      ? parseNumBr(pAliqPis)       || undefined : undefined,
        vPis:           (vBCPisCofins && pAliqPis)
          ? (parseNumBr(vBCPisCofins) * parseNumBr(pAliqPis) / 100) || undefined
          : undefined,
        pAliqCofins:    pAliqCofins   ? parseNumBr(pAliqCofins)    || undefined : undefined,
        vCofins:        (vBCPisCofins && pAliqCofins)
          ? (parseNumBr(vBCPisCofins) * parseNumBr(pAliqCofins) / 100) || undefined
          : undefined,
        descContSocRet: descContSocRet.trim() || undefined,
        vIRRF:          vIRRF       ? parseNumBr(vIRRF)       || undefined : undefined,
        vContSocRet:    vContSocRet ? parseNumBr(vContSocRet) || undefined : undefined,
        vRetCP:         vRetCP      ? parseNumBr(vRetCP)      || undefined : undefined,
        // Total dos tributos
        pTotFed: pTotFed ? parseNumBr(pTotFed) || undefined : undefined,
        pTotEst: pTotEst ? parseNumBr(pTotEst) || undefined : undefined,
        pTotMun: pTotMun ? parseNumBr(pTotMun) || undefined : undefined,
        xInfComp: xInfComp.trim() || undefined,
      })
      setResultado(r.data)
      if (r.data.ok && r.data.numero && finParcelas.length > 0 && finContaCod.trim()) {
        try {
          await api.post(`/nfse-nacional/${r.data.numero}/financeiro?empcod=${empresa?.empcod ?? 1}`, {
            parcelas: finParcelas,
            contaCod: finContaCod,
            retencaoTotal: 0,
          })
        } catch (ef: any) {
          console.warn('[Financeiro] Erro ao salvar financeiro:', ef?.response?.data?.error || ef?.message)
        }
      }
    } catch (err: any) {
      setResultado({ ok: false, erro: err.response?.data?.error ?? err.message })
    } finally {
      setEnviando(false)
    }
  }

  function nova() {
    setEtapa(0)
    setCliente(null)
    setEndereco(null)
    setEndCMunToma('')
    setXDescServ('')
    setCompetencia(dataAtual())
    setCTribNac('')
    setCTribMun('')
    setServicoId(null)
    setServicoDes('')
    setCTribNac('')
    setCTribMun('')
    setCNBS('')
    setCLocPrestCod('')
    setCLocPrestDes('')
    setCLocPrest('')
    setCLocPrestUf('')
    setSerie('7000')
    setNDPS('')
    setFinParcelas([])
    setFinContaCod('')
    setFinContaNome('')
    setFinQtdParcelas(1)
    setFinIntervalo(0)
    setEndLogradouro('')
    setEndNumero('SN')
    setEndBairro('')
    setEndCep('')
    setVServ('')
    setPAliq('')
    setTpRetISSQN('1')
    setPiscofinsCst('07')
    setVBCPisCofins('')
    setPAliqPis('')
    setPAliqCofins('')
    setDescContSocRet('')
    setVIRRF('')
    setVContSocRet('')
    setVRetCP('')
    setPInss('')
    setPTotFed('')
    setPTotEst('')
    setPTotMun('')
    setXInfComp('')
    setResultado(null)
    setBusca('')
    setClientes([])
  }

  // INSS retido = valor do serviço × % do serviço (transporte de passageiros: 11% sobre 30% = 3,3%)
  useEffect(() => {
    if (!pInss.trim()) return
    const v = parseNumBr(vServ) * parseNumBr(pInss) / 100
    setVRetCP(v > 0 ? v.toFixed(2) : '')
  }, [vServ, pInss])

  const vISS = (() => {
    const v = parseNumBr(vServ) || 0
    const a = (parseNumBr(pAliq) / 100) || 0
    return (v * a).toFixed(2)
  })()

  function avancarEtapa2() {
    const faltando: string[] = []
    if (!cLocPrest.trim())                      faltando.push('Local de Prestação')
    if (!cTribNac.trim())                       faltando.push('Serviço (Cód. Tributação Nacional)')
    if (!parseNumBr(vServ))       faltando.push('Valor do Serviço')
    if (!xDescServ.trim())                      faltando.push('Descrição do Serviço')
    if (faltando.length > 0) {
      setErroEtapa1(`Preencha os campos obrigatórios: ${faltando.join(', ')}`)
      return
    }
    setErroEtapa1(null)
    setEtapa(2)
  }

  function handleCstChange(cst: string) {
    setPiscofinsCst(cst)
    if (['01', '02', '03'].includes(cst)) {
      // Lucro Real — não-cumulativo
      setPAliqPis('1.65')
      setPAliqCofins('7.60')
    } else if (['06'].includes(cst)) {
      // Alíquota zero
      setPAliqPis('0.00')
      setPAliqCofins('0.00')
    } else if (['07', '08', '09'].includes(cst)) {
      // Isenta / sem incidência / suspensão — limpa tudo
      setVBCPisCofins('')
      setPAliqPis('')
      setPAliqCofins('')
    }
  }

  const vPisCalc    = (vBCPisCofins && pAliqPis)    ? (parseNumBr(vBCPisCofins) * parseNumBr(pAliqPis)    / 100).toFixed(2) : ''
  const vCofinsCalc = (vBCPisCofins && pAliqCofins) ? (parseNumBr(vBCPisCofins) * parseNumBr(pAliqCofins) / 100).toFixed(2) : ''

  return (
    <>
      <AvisoUltimaNota empcod={empresa?.empcod ?? 1} modelo="99" serie={serie} proximo={nDPS} nacional />
      {/* Stepper */}
      {!resultado && (
        <div className="stepper" style={{ maxWidth: 760 }}>
          {['Tomador', 'Serviço', 'Revisão'].map((l, i) => (
            <div
              key={i}
              className={`step${etapa === i ? ' step--active' : i < etapa ? ' step--done' : ''}`}
              onClick={() => { if (i < etapa || (i === 1 && cliente !== null)) setEtapa(i) }}
              style={{ cursor: (i < etapa || (i === 1 && cliente !== null)) ? 'pointer' : 'default' }}
            >
              <div className="step-circle">{i < etapa ? '✓' : i + 1}</div>
              <span>{l}</span>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ maxWidth: 760 }}>

        {/* ── Etapa 0: Tomador ────────────────────────────────────────────────── */}
        {!resultado && etapa === 0 && (
          <div>
            <h3 className="etapa-titulo">Selecionar Tomador</h3>

            {!cliente && (
              <div className="busca-cliente-wrap">
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Nome, nome fantasia ou CNPJ/CPF</label>
                  <div className="busca-cliente-row">
                    <input
                      type="text"
                      placeholder="Digite para buscar..."
                      value={busca}
                      onChange={e => handleBusca(e.target.value)}
                      autoFocus
                    />
                    {buscando && <span className="busca-spinner">Buscando...</span>}
                    <button className="btn btn-primary btn-sm" onClick={() => setModalNovo(true)} style={{ whiteSpace: 'nowrap' }}>
                      + Novo Cliente
                    </button>
                  </div>
                </div>
              </div>
            )}

            {modalNovo && (
              <NovoClienteModal
                onClose={() => setModalNovo(false)}
                onSalvo={c => { setModalNovo(false); selecionarCliente(c) }}
              />
            )}

            {clientes.length > 0 && !cliente && (
              <div className="lista-clientes">
                {clientes.map(c => (
                  <button key={c.cli_cod} className="item-cliente" onClick={() => selecionarCliente(c)}>
                    <div className="item-cliente-linha1">
                      <strong>{c.cli_nom}</strong>
                      <span className="badge-tipo">{c.cli_fj === 'J' ? 'PJ' : 'PF'}</span>
                    </div>
                    {c.cli_fan && c.cli_fan !== c.cli_nom && (
                      <span className="item-cliente-fantasia">{c.cli_fan}</span>
                    )}
                    <div className="item-cliente-doc">
                      <span>{c.cli_fj === 'J' ? 'CNPJ' : 'CPF'}: {formatarDocumento(c.cli_mat)}</span>
                      {c.cli_est?.trim() && <span>UF: {c.cli_est}</span>}
                    </div>
                    {(c.end_logradouro?.trim() || c.end_cidade?.trim()) && (
                      <span className="item-cliente-endereco">
                        {[
                          [c.end_logradouro, c.end_numero].filter(Boolean).join(', '),
                          c.end_bairro,
                          c.end_cidade && c.end_uf ? `${c.end_cidade}/${c.end_uf}` : (c.end_cidade || c.end_uf),
                        ].filter(Boolean).join(' — ')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {cliente && (
              <div className="cliente-card">
                <div className="cliente-card-header">
                  <div>
                    <div className="cliente-card-nome">{cliente.cli_nom}</div>
                    {cliente.cli_fan && cliente.cli_fan !== cliente.cli_nom && (
                      <div className="cliente-card-fantasia">{cliente.cli_fan}</div>
                    )}
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={() => { setCliente(null); setEndereco(null); setEndCMunToma('') }}>
                    Trocar
                  </button>
                </div>
                <div className="cliente-card-fiscal">
                  <span><b>{cliente.cli_fj === 'J' ? 'CNPJ' : 'CPF'}:</b> {formatarDocumento(cliente.cli_mat)}</span>
                </div>
                {cliente.bloqueado && (
                  <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 0.75rem', borderRadius: 4, margin: '0.5rem 0' }}>
                    ⚠ CLIENTE BLOQUEADO — não é possível emitir documentos para este cliente.
                  </div>
                )}
              </div>
            )}

            {cliente && (
              <div className="etapa-acoes">
                <button className="btn btn-primary" disabled={cliente.bloqueado} onClick={() => setEtapa(1)}>Próximo →</button>
              </div>
            )}
          </div>
        )}

        {/* ── Etapa 1: Serviço ────────────────────────────────────────────────── */}
        {!resultado && etapa === 1 && (
          <div>
            <h3 className="etapa-titulo">Dados do Serviço</h3>

            {/* Barra azul — campos chave */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end', background: '#1a6ea8', borderRadius: '0.5rem', padding: '0.65rem 1rem', marginBottom: '1rem' }}>
              <div className="field" style={{ flex: '0 0 220px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Empresa Emitente</label>
                <select
                  value={empresa?.empcod ?? ''}
                  onChange={e => setEmpresa(empresas.find(x => x.empcod === Number(e.target.value)) ?? null)}
                  style={{ fontWeight: 600 }}
                >
                  {empresas.map(emp => (
                    <option key={emp.empcod} value={emp.empcod}>
                      {emp.empcod} - {emp.empfan || emp.empnom}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: '0 0 160px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Competência</label>
                <input type="date" value={competencia} onChange={e => setCompetencia(e.target.value)} style={{ fontWeight: 600 }} />
              </div>
              <div className="field" style={{ flex: '0 0 80px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Série</label>
                <input type="text" value={serie} onChange={e => setSerie(e.target.value)} style={{ fontWeight: 600 }} />
              </div>
              <div className="field" style={{ flex: '0 0 110px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }} title="Número interno da DPS. O número da NFS-e é a Sefin que dá.">Nº da DPS *</label>
                <input type="number" min="1" value={nDPS} onChange={e => setNDPS(e.target.value)} placeholder="próximo" style={{ fontWeight: 600 }} />
              </div>
              <div className="field" style={{ flex: '1 1 240px', minWidth: 0, marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Tomador</label>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', background: 'white', borderRadius: '0.45rem', padding: '0.5rem 0.85rem', fontSize: '0.875rem', fontWeight: 600 }}>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cliente?.cli_nom} · {formatarDocumento(cliente?.cli_mat ?? '')}</span>
                  <button className="btn btn-outline btn-sm" onClick={() => setEtapa(0)} style={{ fontSize: '0.72rem', padding: '2px 10px', flexShrink: 0, whiteSpace: 'nowrap' }}>Trocar</button>
                </div>
              </div>
            </div>

            {/* Endereço do tomador */}
            <div style={{ background: '#f8faff', border: '1.5px solid #e0e7ff', borderRadius: '0.5rem', padding: '0.65rem 1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                Endereço do Tomador
              </div>
              <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
                <div className="field" style={{ flex: '0 0 110px', marginBottom: 0 }}>
                  <label>CEP *</label>
                  <input type="text" maxLength={8} value={endCep} onChange={e => setEndCep(e.target.value.replace(/\D/g, ''))} placeholder="00000000" />
                </div>
                <div className="field" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                  <label>Logradouro *</label>
                  <input type="text" value={endLogradouro} onChange={e => setEndLogradouro(e.target.value)} placeholder="Rua / Av." />
                </div>
                <div className="field" style={{ flex: '0 0 90px', marginBottom: 0 }}>
                  <label>Número *</label>
                  <input type="text" value={endNumero} onChange={e => setEndNumero(e.target.value)} placeholder="SN" />
                </div>
                <div className="field" style={{ flex: '1 1 160px', marginBottom: 0 }}>
                  <label>Bairro *</label>
                  <input type="text" value={endBairro} onChange={e => setEndBairro(e.target.value)} placeholder="Bairro" />
                </div>
              </div>
            </div>

            {/* Serviço + ISS */}
            <div className="form-grid" style={{ gap: '0.8rem', marginBottom: '1rem' }}>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Serviço (Cód. de Tributação Nacional)</label>
                <ServicoNFSeSelect
                  value={servicoId}
                  descricao={servicoDes}
                  onChange={s => {
                    if (s) {
                      setServicoId(s.id); setServicoDes(s.descricao)
                      setCTribNac(s.cod_tributacao_nacional); setCTribMun(s.cod_tributacao_municipal)
                      setCTribMunPadrao(s.cod_tributacao_municipal)
                      setCNBS(s.nbs); setPAliq(s.aliquota_iss); setPAliqPadrao(s.aliquota_iss)
                      setPInss(Number(s.perc_inss) > 0 ? Number(s.perc_inss).toFixed(2) : '')
                      setPiscofinsCst(s.piscofins_cst || '07')
                    } else {
                      setServicoId(null); setServicoDes('')
                      setCTribNac(''); setCTribMun(''); setCTribMunPadrao(''); setPAliqPadrao(''); setCNBS(''); setPInss('')
                    }
                  }}
                />
              </div>

              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Descrição do Serviço *</label>
                <textarea rows={3} value={xDescServ} onChange={e => setXDescServ(e.target.value)}
                  placeholder="Descreva o serviço prestado" style={{ resize: 'vertical' }} />
              </div>

              <div className="field">
                <label>Local de Prestação</label>
                <CidadeSelect
                  codValue={cLocPrestCod} desValue={cLocPrestDes}
                  ibgeValue={cLocPrest}   ufValue={cLocPrestUf}
                  filtroUf="RS"
                  onChange={(cod, des, ibge, uf) => { setCLocPrestCod(cod); setCLocPrestDes(des); setCLocPrest(ibge); setCLocPrestUf(uf) }}
                />
              </div>

              <div className="field">
                <label>Código de Tributação Municipal</label>
                <input type="text" value={cTribMun} onChange={e => setCTribMun(e.target.value)}
                  placeholder="Se o município exigir" />
              </div>

              <div className="field">
                <label>Retenção ISS</label>
                <select value={tpRetISSQN} onChange={e => setTpRetISSQN(e.target.value as '1' | '2')}>
                  <option value="1">Não retido pelo tomador</option>
                  <option value="2">Retido pelo tomador</option>
                </select>
              </div>

              <div className="field">
                <label>Valor do Serviço (R$) *</label>
                <input type="text" placeholder="0,00" value={vServ} onChange={e => setVServ(e.target.value)} />
              </div>

              <div className="field">
                <label>Alíquota ISS (%)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="text" placeholder="5.00" value={pAliq} onChange={e => setPAliq(e.target.value)} />
                  <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.85rem', fontWeight: 700, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                    ISS R$ {vISS}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Tributação Federal ─────────────────────────────────────────── */}
            <div style={{ background: '#f8faff', border: '1.5px solid #e0e7ff', borderRadius: '0.6rem', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.85rem' }}>
                Tributação Federal
              </div>

              {/* PIS/COFINS */}
              <div style={{ borderLeft: '3px solid #6366f1', paddingLeft: '0.85rem', marginBottom: '0.85rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>PIS / COFINS</div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div className="field" style={{ flex: '1 1 280px', marginBottom: 0 }}>
                    <label>Situação tributária (CST)</label>
                    <select value={piscofinsCst} onChange={e => handleCstChange(e.target.value)}>
                      {CST_PIS_COFINS.map(c => (
                        <option key={c.cod} value={c.cod}>{c.cod} — {c.desc}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field" style={{ flex: '0 0 160px', marginBottom: 0 }}>
                    <label>Base de cálculo (R$)</label>
                    <input type="text" value={vBCPisCofins} onChange={e => setVBCPisCofins(e.target.value)} placeholder="0,00" />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
                  <div className="field" style={{ flex: '0 0 130px', marginBottom: 0 }}>
                    <label>PIS — Alíq. (%)</label>
                    <input type="text" value={pAliqPis} onChange={e => setPAliqPis(e.target.value)} placeholder="0.65" />
                  </div>
                  <div className="field" style={{ flex: '0 0 130px', marginBottom: 0 }}>
                    <label>PIS — Débito (R$)</label>
                    <input type="text" readOnly value={vPisCalc} placeholder="calculado" style={{ background: '#eef2ff', color: '#3730a3', fontWeight: 600 }} />
                  </div>
                  <div className="field" style={{ flex: '0 0 130px', marginBottom: 0 }}>
                    <label>COFINS — Alíq. (%)</label>
                    <input type="text" value={pAliqCofins} onChange={e => setPAliqCofins(e.target.value)} placeholder="3.00" />
                  </div>
                  <div className="field" style={{ flex: '0 0 130px', marginBottom: 0 }}>
                    <label>COFINS — Débito (R$)</label>
                    <input type="text" readOnly value={vCofinsCalc} placeholder="calculado" style={{ background: '#eef2ff', color: '#3730a3', fontWeight: 600 }} />
                  </div>
                </div>
              </div>

              {/* Retenções */}
              <div style={{ borderLeft: '3px solid #f59e0b', paddingLeft: '0.85rem', marginBottom: '0.85rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Retenções</div>
                <div className="field" style={{ marginBottom: '0.6rem' }}>
                  <label>Descrição Contribuições Sociais — Retidas</label>
                  <input type="text" value={descContSocRet} onChange={e => setDescContSocRet(e.target.value)}
                    placeholder="Ex: Retenção CSLL conforme contrato..." />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div className="field" style={{ flex: '1 1 150px', marginBottom: 0 }}>
                    <label>IRRF (R$)</label>
                    <input type="text" value={vIRRF} onChange={e => setVIRRF(e.target.value)} placeholder="0,00" />
                  </div>
                  <div className="field" style={{ flex: '1 1 180px', marginBottom: 0 }}>
                    <label>Contrib. Sociais Retidas (R$)</label>
                    <input type="text" value={vContSocRet} onChange={e => setVContSocRet(e.target.value)} placeholder="0,00" />
                  </div>
                  <div className="field" style={{ flex: '0 0 110px', marginBottom: 0 }}>
                    <label>INSS (%)</label>
                    <input type="text" value={pInss} onChange={e => setPInss(e.target.value)} placeholder="3.30" />
                  </div>
                  <div className="field" style={{ flex: '1 1 180px', marginBottom: 0 }}>
                    <label>CP Retida / INSS (R$)</label>
                    <input type="text" value={vRetCP} onChange={e => setVRetCP(e.target.value)} placeholder="0,00" />
                  </div>
                </div>
              </div>

              {/* Total dos tributos */}
              <div style={{ borderLeft: '3px solid #10b981', paddingLeft: '0.85rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Total dos tributos (%)</div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div className="field" style={{ flex: '1 1 130px', marginBottom: 0 }}>
                    <label>Federal (%)</label>
                    <input type="text" value={pTotFed} onChange={e => setPTotFed(e.target.value)} placeholder="3.65" />
                  </div>
                  <div className="field" style={{ flex: '1 1 130px', marginBottom: 0 }}>
                    <label>Estadual (%)</label>
                    <input type="text" value={pTotEst} onChange={e => setPTotEst(e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="field" style={{ flex: '1 1 130px', marginBottom: 0 }}>
                    <label>Municipal (%)</label>
                    <input type="text" value={pTotMun} onChange={e => setPTotMun(e.target.value)} placeholder="2.00" />
                  </div>
                </div>
              </div>
            </div>

            {/* Informações complementares */}
            <div className="field">
              <label>Informações complementares</label>
              <textarea rows={2} value={xInfComp} onChange={e => setXInfComp(e.target.value)}
                placeholder="Observações adicionais para a NFS-e..." style={{ resize: 'vertical' }} />
            </div>

            {erroEtapa1 && (
              <div style={{ color: '#dc2626', fontSize: '0.82rem', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 4, padding: '6px 10px', marginBottom: '0.5rem' }}>
                {erroEtapa1}
              </div>
            )}
            <div className="etapa-acoes">
              <button className="btn btn-outline" onClick={() => setEtapa(0)}>← Voltar</button>
              <button className="btn btn-primary" onClick={avancarEtapa2}>
                Avançar →
              </button>
            </div>
          </div>
        )}

        {/* ── Etapa 2: Revisão e Emissão ──────────────────────────────────── */}
        {!resultado && etapa === 2 && (() => {
          const brl    = (v: number | string) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          const vTotal = parseNumBr(vServ) || 0
          const vISSNum = Number(vISS) || 0
          const secStyle: React.CSSProperties = { marginBottom: '1rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem 1rem' }
          const labelStyle: React.CSSProperties = { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: '#6b7280', marginBottom: '0.4rem' }
          const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.18rem 0', borderBottom: '1px dashed #e5e7eb' }
          const n    = (s: string) => parseNumBr(s)
          const vPisRet    = n(vBCPisCofins) > 0 && n(pAliqPis)    ? n(vBCPisCofins) * n(pAliqPis)    / 100 : 0
          const vCofinsRet = n(vBCPisCofins) > 0 && n(pAliqCofins) ? n(vBCPisCofins) * n(pAliqCofins) / 100 : 0
          const vIRRFNum   = n(vIRRF)
          const vContSocNum= n(vContSocRet)
          const vRetCPNum  = n(vRetCP)
          const vISSRet    = tpRetISSQN === '2' ? vISSNum : 0
          const totalRet   = vISSRet + vPisRet + vCofinsRet + vIRRFNum + vContSocNum + vRetCPNum
          const vLiquido   = vTotal - totalRet

          const impostos = [
            { nome: 'ISS',                  base: vTotal,          aliq: n(pAliq),         valor: vISSNum,    retido: tpRetISSQN === '2', show: vISSNum > 0 },
            { nome: 'PIS',                  base: n(vBCPisCofins), aliq: n(pAliqPis),      valor: vPisRet,    retido: true,               show: vPisRet > 0 },
            { nome: 'COFINS',               base: n(vBCPisCofins), aliq: n(pAliqCofins),   valor: vCofinsRet, retido: true,               show: vCofinsRet > 0 },
            { nome: 'IRRF',                 base: 0,               aliq: 0,                valor: vIRRFNum,   retido: true,               show: vIRRFNum > 0 },
            { nome: 'CSLL/PIS/COF ret.',    base: 0,               aliq: 0,                valor: vContSocNum,retido: true,               show: vContSocNum > 0 },
            { nome: 'INSS retido',          base: 0,               aliq: 0,                valor: vRetCPNum,  retido: true,               show: vRetCPNum > 0 },
          ].filter(x => x.show)

          return (
            <div>
              <h3 className="etapa-titulo">Revisão e Emissão</h3>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                {/* Tomador */}
                <div style={{ ...secStyle, flex: '1 1 220px' }}>
                  <div style={labelStyle}>Tomador</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{cliente?.cli_nom}</div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                    {cliente?.cli_mat.replace(/\D/g, '').replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5').replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')}
                  </div>
                  {endLogradouro && (
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{endLogradouro}, {endNumero} — {endBairro}</div>
                  )}
                </div>
                {/* Documento */}
                <div style={{ ...secStyle, flex: '1 1 180px' }}>
                  <div style={labelStyle}>Documento</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>NFS-e Nacional · Série {serie} · DPS {nDPS}</div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Competência: {competencia}</div>
                  {cTribNac && <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Cód. Tributação: {cTribNac}</div>}
                </div>
                {/* Totais */}
                <div style={{ ...secStyle, flex: '0 0 auto', minWidth: 160 }}>
                  <div style={labelStyle}>Total do Serviço</div>
                  <div style={{ fontWeight: 800, fontSize: '1.3rem', color: '#f07820', fontVariantNumeric: 'tabular-nums' }}>{brl(vTotal)}</div>
                  {totalRet > 0 && (
                    <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                      (-) Retenções: {brl(totalRet)}
                    </div>
                  )}
                  {totalRet > 0 && (
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#15803d', marginTop: 4, borderTop: '1px solid #e5e7eb', paddingTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                      Líquido: {brl(vLiquido)}
                    </div>
                  )}
                </div>
              </div>

              {/* Serviço */}
              <div style={secStyle}>
                <div style={labelStyle}>Serviço</div>
                <div style={rowStyle}>
                  <span style={{ fontWeight: 600 }}>{xDescServ}</span>
                </div>
                {servicoDes && (
                  <div style={{ ...rowStyle, color: '#6b7280', fontSize: '0.78rem' }}>
                    <span>{servicoDes}</span>
                  </div>
                )}
                <div style={{ ...rowStyle, color: tpRetISSQN === '2' ? '#b45309' : '#6b7280', fontSize: '0.78rem' }}>
                  <span>{tpRetISSQN === '2' ? 'ISS retido pelo tomador' : 'ISS não retido pelo tomador'}</span>
                </div>
              </div>

              {/* Impostos */}
              {impostos.length > 0 && (
                <div style={secStyle}>
                  <div style={labelStyle}>Impostos e Retenções</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ background: '#f3f4f6' }}>
                        <th style={{ padding: '4px 8px', border: '1px solid #e5e7eb', textAlign: 'left', fontWeight: 700, color: '#6b7280' }}>Tributo</th>
                        <th style={{ padding: '4px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700, color: '#6b7280', width: 120 }}>Base de Cálculo</th>
                        <th style={{ padding: '4px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700, color: '#6b7280', width: 70 }}>Alíquota</th>
                        <th style={{ padding: '4px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700, color: '#6b7280', width: 120 }}>Valor</th>
                        <th style={{ padding: '4px 8px', border: '1px solid #e5e7eb', textAlign: 'center', fontWeight: 700, color: '#6b7280', width: 70 }}>Retido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {impostos.map((imp, i) => (
                        <tr key={i}>
                          <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb', fontWeight: 700 }}>{imp.nome}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{imp.base > 0 ? brl(imp.base) : '—'}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>{imp.aliq > 0 ? `${imp.aliq.toFixed(2)}%` : '—'}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: imp.retido ? '#dc2626' : '#374151' }}>{brl(imp.valor)}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: imp.retido ? '#fee2e2' : '#f3f4f6', color: imp.retido ? '#dc2626' : '#9ca3af' }}>
                              {imp.retido ? 'Sim' : 'Não'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {totalRet > 0 && (
                      <tfoot>
                        <tr style={{ background: '#fef9c3' }}>
                          <td colSpan={3} style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: 700, textAlign: 'right' }}>Total de Retenções</td>
                          <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{brl(totalRet)}</td>
                          <td style={{ border: '1px solid #e5e7eb' }} />
                        </tr>
                        <tr className="thead-claro" style={{ background: '#f0fdf4' }}>
                          <td colSpan={3} style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: 700, textAlign: 'right' }}>Valor Líquido a Receber</td>
                          <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 800, color: '#15803d', fontSize: '0.9rem', fontVariantNumeric: 'tabular-nums' }}>{brl(vLiquido)}</td>
                          <td style={{ border: '1px solid #e5e7eb' }} />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}

              <div className="etapa-acoes">
                <button className="btn btn-secondary" onClick={() => setEtapa(1)}>← Voltar</button>
                <button
                  onClick={() => {
                    const base = new Date(competencia)
                    setFinParcelas(Array.from({ length: finQtdParcelas }, (_, i) => {
                      const d = new Date(base)
                      d.setDate(d.getDate() + finIntervalo * (i + 1))
                      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (vTotal / finQtdParcelas).toFixed(2) }
                    }))
                    setFinanceiroModal(true)
                  }}
                  style={{ background: '#065f46', color: '#fff', border: 'none', borderRadius: 6, padding: '0.35rem 0.9rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  {finParcelas.length > 0 ? 'Financeiro ✓' : '+ Financeiro'}
                </button>
                <button className="btn btn-primary" disabled={enviando || cliente?.bloqueado} onClick={emitir}>
                  {enviando ? 'Emitindo...' : editId ? 'Salvar e Transmitir' : 'Emitir NFS-e'}
                </button>
              </div>
            </div>
          )
        })()}

        {/* ── Resultado ───────────────────────────────────────────────────────── */}
        {resultado && (
          <div>
            {resultado.ok ? (
              <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#15803d', marginBottom: 12 }}>
                  NFS-e emitida com sucesso!
                </div>
                {(resultado.serie || resultado.numero) && (
                  <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: 8 }}>
                    NFS-e Nº {resultado.numero}{resultado.nDPS ? ` · DPS ${resultado.nDPS} (série ${resultado.serie})` : ''}
                  </div>
                )}
                {resultado.avisoBanco && (
                  <div style={{ background: '#fef2f2', border: '1px solid #dc2626', borderRadius: 6, padding: '10px 16px', fontSize: '0.86rem', fontWeight: 700, color: '#991b1b', margin: '0 auto 10px', maxWidth: 600 }}>
                    {resultado.avisoBanco}
                  </div>
                )}
                {resultado.chaveAcesso && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '10px 16px', fontSize: '0.82rem', fontFamily: 'monospace', wordBreak: 'break-all', margin: '0 auto', maxWidth: 600, color: '#166534' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Chave de Acesso</div>
                    {resultado.chaveAcesso}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>❌</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#b91c1c', marginBottom: 12 }}>
                  Erro na emissão
                </div>
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 16px', fontSize: '0.88rem', color: '#991b1b', margin: '0 auto', maxWidth: 600 }}>
                  {resultado.xMotivo ?? resultado.erro ?? 'Erro desconhecido'}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.2rem', gap: 10 }}>
              <button className="btn btn-primary" onClick={nova}>Nova NFS-e</button>
              {!resultado.ok && (
                <button className="btn btn-outline" onClick={() => { setResultado(null); setEtapa(1) }}>
                  Corrigir e Reenviar
                </button>
              )}
            </div>
          </div>
        )}

      </div>

    {/* ── Modal Financeiro ── */}
    {financeiroModal && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        zIndex: 1000, padding: '24px 12px',
      }} onClick={() => setFinanceiroModal(false)}>
        <div style={{
          background: '#fff', borderRadius: 8, width: 560, maxWidth: '96vw',
          maxHeight: 'calc(100vh - 48px)', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }} onClick={e => e.stopPropagation()}>
          <div style={{ background: '#065f46', color: '#fff', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>Financeiro — NFS-e Nacional</div>
              <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 2 }}>
                Série {serie} · DPS {nDPS} — Total: {(parseNumBr(vServ) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </div>
              {finCondDes && (
                <div style={{ fontSize: '0.75rem', opacity: 0.85, marginTop: 2 }}>
                  Condição de Pagamento: {finCondDes}
                </div>
              )}
            </div>
            <button onClick={() => setFinanceiroModal(false)} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '1rem' }}>✕</button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Qtd. Parcelas</label>
                <input type="number" min={1} max={36} value={finQtdParcelas}
                  onChange={e => {
                    const qtd = Math.max(1, Math.min(36, Number(e.target.value)))
                    setFinQtdParcelas(qtd)
                    const base = new Date(competencia)
                    const vTotal = parseNumBr(vServ) || 0
                    setFinParcelas(Array.from({ length: qtd }, (_, i) => {
                      const d = new Date(base)
                      d.setDate(d.getDate() + finIntervalo * (i + 1))
                      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (vTotal / qtd).toFixed(2) }
                    }))
                  }}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Intervalo (dias)</label>
                <input type="number" min={0} value={finIntervalo}
                  onChange={e => {
                    const intervalo = Math.max(0, Number(e.target.value))
                    setFinIntervalo(intervalo)
                    const base = new Date(competencia)
                    const vTotal = parseNumBr(vServ) || 0
                    setFinParcelas(Array.from({ length: finQtdParcelas }, (_, i) => {
                      const d = new Date(base)
                      d.setDate(d.getDate() + intervalo * (i + 1))
                      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (vTotal / finQtdParcelas).toFixed(2) }
                    }))
                  }}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Conta Financeira *</label>
              <div style={{ display: 'flex', gap: '0.5rem', position: 'relative' }}>
                <input type="text" value={finContaCod}
                  onChange={e => { setFinContaCod(e.target.value); setFinContaNome('') }}
                  onBlur={async e => {
                    const cod = e.target.value.trim()
                    if (!cod) return
                    const r = await api.get('/erp/contas', { params: { busca: cod } })
                    const found = r.data.find((c: any) => c.dep_cod.trim() === cod)
                    if (found) setFinContaNome(found.dep_des)
                  }}
                  placeholder="Código" autoComplete="off"
                  style={{ width: 80, border: `1px solid ${finContaCod.trim() ? '#d1d5db' : '#fca5a5'}`, borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
                <div style={{ flex: 1, position: 'relative' }}>
                  {finContaCod && finContaNome ? (
                    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #6ee7b7', borderRadius: 4, padding: '6px 8px', background: '#f0fdf4', fontSize: '0.85rem' }}>
                      <span style={{ flex: 1, color: '#065f46', fontWeight: 600 }}>{finContaNome}</span>
                      <button onClick={() => { setFinContaCod(''); setFinContaNome('') }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '0 2px', fontSize: '1rem', lineHeight: 1 }}>×</button>
                    </div>
                  ) : (
                    <>
                      <input type="text" value={finContaNome}
                        onChange={async e => {
                          const v = e.target.value
                          setFinContaNome(v)
                          setFinContaCod('')
                          if (v.length >= 2) {
                            const r = await api.get('/erp/contas', { params: { busca: v } })
                            setFinContaResultados(r.data)
                            setFinContaAberto(true)
                          } else {
                            setFinContaResultados([])
                            setFinContaAberto(false)
                          }
                        }}
                        onBlur={() => setTimeout(() => setFinContaAberto(false), 150)}
                        placeholder="Digite para buscar..." autoComplete="off"
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
                      {finContaAberto && finContaResultados.length > 0 && (
                        <div style={{ position: 'absolute', zIndex: 50, background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', width: '100%', maxHeight: 200, overflowY: 'auto', top: '100%', left: 0 }}>
                          {finContaResultados.map(c => (
                            <div key={c.dep_cod} onMouseDown={() => { setFinContaCod(c.dep_cod.trim()); setFinContaNome(c.dep_des); setFinContaAberto(false) }}
                              style={{ padding: '7px 10px', cursor: 'pointer', fontSize: '0.82rem', borderBottom: '1px solid #f3f4f6' }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#f0fdf4')}
                              onMouseLeave={e => (e.currentTarget.style.background = '')}>
                              <span style={{ fontFamily: 'monospace', fontWeight: 700, marginRight: 8, color: '#374151' }}>{c.dep_cod.trim()}</span>
                              <span style={{ color: '#6b7280' }}>{c.dep_des}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {finParcelas.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr className="thead-claro" style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'center', width: 60 }}>Parcela</th>
                    <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Vencimento</th>
                    <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Valor (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {finParcelas.map((p, i) => (
                    <tr key={i}>
                      <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'center', fontWeight: 700, color: '#6b7280' }}>{p.numero}</td>
                      <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>
                        <input type="date" value={p.vencimento}
                          onChange={e => setFinParcelas(prev => prev.map((x, j) => j === i ? { ...x, vencimento: e.target.value } : x))}
                          style={{ border: 'none', background: 'transparent', fontSize: '0.82rem', width: '100%' }} />
                      </td>
                      <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>
                        <input type="number" value={p.valor}
                          onChange={e => setFinParcelas(prev => prev.map((x, j) => j === i ? { ...x, valor: e.target.value } : x))}
                          style={{ border: 'none', background: 'transparent', fontSize: '0.82rem', textAlign: 'right', width: '100%' }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="thead-claro" style={{ background: '#f9fafb', fontWeight: 700 }}>
                    <td colSpan={2} style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Total</td>
                    <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', color: '#065f46' }}>
                      {finParcelas.reduce((s, p) => s + Number(p.valor), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.65rem 1.25rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', background: '#f9fafb' }}>
            <button onClick={() => { setFinParcelas([]); setFinContaCod(''); setFinContaNome(''); setFinanceiroModal(false) }}
              style={{ padding: '5px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.82rem', cursor: 'pointer', color: '#6b7280' }}>
              Limpar
            </button>
            <button onClick={() => setFinanceiroModal(false)}
              disabled={finParcelas.length > 0 && !finContaCod.trim()}
              style={{ padding: '5px 18px', background: '#065f46', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.82rem', fontWeight: 600, cursor: finParcelas.length > 0 && !finContaCod.trim() ? 'not-allowed' : 'pointer', opacity: finParcelas.length > 0 && !finContaCod.trim() ? 0.6 : 1 }}>
              Confirmar
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
